import { HseInstanceProvider } from "@dashboard/contexts/HseInstanceProvider";
import { useSelectedCustomer } from "@dashboard/contexts/selectedCustomerContext";
import ErrorPage from "@dashboard/ErrorPage";
import { useTaskAccess } from "@dashboard/hooks/useTaskAccess";
import { useTaskAbility } from "@dashboard/hooks/useTaskAbility";
import { isAxiosError } from "axios";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createTaskAbility, TaskAbilityContext } from "@dashboard/casl/taskAbility";

interface HasHseAccessProps {
    children: ReactNode;
    fallback: ReactNode;
}

export function HasHseAccess({ children, fallback }: HasHseAccessProps) {
    const { t } = useTranslation();
    const { dashboardNavigation, isDashboardNavigationPending } = useSelectedCustomer();
    const { isPending, data, isError, error } = useTaskAccess();
    const {
        isPending: isAbilityPending,
        data: abilityData,
        isError: isAbilityError,
        error: abilityError,
    } = useTaskAbility();

    const ability = useMemo(
        () => createTaskAbility(abilityData?.rules ?? []),
        [abilityData?.rules],
    );

    if (isPending || isAbilityPending || isDashboardNavigationPending) {
        return (
            <div className="flex items-center justify-center h-screen bg-background text-on-background">
                {t("common.loading")}
            </div>
        );
    }

    if (isError || isAbilityError) {
        const requestError = error ?? abilityError;
        const statusCode = isAxiosError(requestError) ? (requestError.code ?? "500") : "500";

        return <ErrorPage statusCode={statusCode} />;
    }

    if (!data || data.length <= 0 || !dashboardNavigation) {
        return <>{fallback}</>;
    }

    const taskInstance = data.find(
        (access) =>
            access.productInstanceId === dashboardNavigation.productInstanceId &&
            access.instanceId === dashboardNavigation.taskInstanceId,
    );

    if (!taskInstance) {
        return <>{fallback}</>;
    }

    return (
        <TaskAbilityContext.Provider value={ability}>
            <HseInstanceProvider
                key={taskInstance.instanceId}
                instanceId={taskInstance.instanceId}
                productInstanceId={taskInstance.productInstanceId}
            >
                {children}
            </HseInstanceProvider>
        </TaskAbilityContext.Provider>
    );
}
